import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Users,
    X,
    Info,
    AlertTriangle,
    Clock,
    CalendarDays,
    Briefcase,
    ChevronRight,
    ChevronLeft,
    ChevronsLeft,
    ChevronsRight,
    UserX,
    CheckCircle2,
    Search
} from 'lucide-react';

const TeamStatusWidget = ({ teamStatus, isEditMode, onOpenEmployeeModal }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTab, setSelectedTab] = useState('All');
    const [nameQuery, setNameQuery] = useState('');
    const [deptQuery, setDeptQuery] = useState('');
    const [statusQuery, setStatusQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);

    const {
        totalTeamMembers = 0,
        presentCount = 0,
        absentCount = 0,
        lateCount = 0,
        onLeaveCount = 0,
        onReportCount = 0,
        attendanceRiskCount = 0,
        outOfOfficeCount = 0,
        previewItems = [],
        allDetailItems = []
    } = teamStatus || {};

    const isAllClear = previewItems.length === 0;

    const getStatusConfig = (statusType) => {
        switch (statusType) {
            case 'Absent': return { icon: UserX, color: 'text-red-600 dark:text-red-400', bg: 'bg-red-100 dark:bg-red-900/40', badge: 'bg-red-100 text-red-700 dark:bg-red-900/60 dark:text-red-300' };
            case 'MissingRecord': return { icon: AlertTriangle, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-100 dark:bg-orange-900/40', badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-300' };
            case 'Late': return { icon: Clock, color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-100 dark:bg-amber-900/40', badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300' };
            case 'OnLeave':
            case 'OnReport': return { icon: CalendarDays, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-100 dark:bg-blue-900/40', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300' };
            case 'OutOfOffice': return { icon: Briefcase, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-100 dark:bg-purple-900/40', badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/60 dark:text-purple-300' };
            default: return { icon: Info, color: 'text-gray-600 dark:text-gray-400', bg: 'bg-gray-100 dark:bg-gray-800', badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' };
        }
    };

    const tabs = useMemo(() => [
        { id: 'All', label: 'Tümü', count: allDetailItems.length },
        { id: 'Absent', label: 'Gelmeyen', count: absentCount },
        { id: 'MissingRecord', label: 'Riskli', count: attendanceRiskCount },
        { id: 'Late', label: 'Geç', count: lateCount },
        { id: 'OnLeave', label: 'İzinli/Raporlu', count: onLeaveCount + onReportCount },
        { id: 'OutOfOffice', label: 'Dış Görev', count: outOfOfficeCount }
    ].filter(t => t.id !== 'OutOfOffice' || t.count > 0), [
        allDetailItems.length,
        absentCount,
        attendanceRiskCount,
        lateCount,
        onLeaveCount,
        onReportCount,
        outOfOfficeCount
    ]);

    const filteredItems = useMemo(() => {
        const nameQ = nameQuery.trim().toLocaleLowerCase('tr-TR');
        const deptQ = deptQuery.trim().toLocaleLowerCase('tr-TR');
        const statusQ = statusQuery.trim().toLocaleLowerCase('tr-TR');

        return allDetailItems.filter(item => {
            if (selectedTab === 'OnLeave') {
                if (item.statusType !== 'OnLeave' && item.statusType !== 'OnReport') return false;
            } else if (selectedTab !== 'All' && item.statusType !== selectedTab) {
                return false;
            }

            if (nameQ && !(item.fullName || '').toLocaleLowerCase('tr-TR').includes(nameQ)) return false;
            if (deptQ && !(item.departmentName || '').toLocaleLowerCase('tr-TR').includes(deptQ)) return false;
            if (statusQ) {
                const statusText = [
                    item.statusLabel,
                    item.statusType === 'Late' ? 'Geç' : '',
                    item.shortDescription,
                    item.shiftLabel
                ].filter(Boolean).join(' ').toLocaleLowerCase('tr-TR');
                if (!statusText.includes(statusQ)) return false;
            }
            return true;
        });
    }, [allDetailItems, selectedTab, nameQuery, deptQuery, statusQuery]);

    const totalPages = Math.max(1, Math.ceil(filteredItems.length / itemsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * itemsPerPage;
    const paginatedItems = useMemo(
        () => filteredItems.slice(startIndex, startIndex + itemsPerPage),
        [filteredItems, startIndex, itemsPerPage]
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [selectedTab, nameQuery, deptQuery, statusQuery, itemsPerPage]);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const openModal = (tab = 'All') => {
        if (isEditMode) return;
        setSelectedTab(tab);
        setNameQuery('');
        setDeptQuery('');
        setStatusQuery('');
        setCurrentPage(1);
        setIsModalOpen(true);
    };

    if (!teamStatus) return null;

    return (
        <>
            <motion.div
                layout={!isEditMode}
                initial={isEditMode ? false : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={isEditMode ? undefined : { opacity: 0, scale: 0.9 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden relative group transition-all hover:shadow-2xl h-full flex flex-col"
            >
                {/* Decorative background blur */}
                <div className="absolute top-0 right-0 -mr-8 -mt-8 w-32 h-32 rounded-full bg-blue-500/10 blur-2xl pointer-events-none"></div>

                {/* Header */}
                <div className="p-3 pb-2 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-2 justify-between items-center bg-gray-50/50 dark:bg-gray-800/10 shrink-0">
                    <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-md shadow-inner text-white">
                            <Users size={16} />
                        </div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">Ekip Durumu</h3>
                    </div>

                    <div className="flex flex-wrap flex-1 items-center justify-end gap-1.5 sm:gap-2 ml-4">
                        <div
                            onClick={() => onOpenEmployeeModal ? onOpenEmployeeModal('totalEmployees', 'Toplam Personel') : openModal('All')}
                            className="flex items-center px-2 py-0.5 rounded-md bg-gray-100 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            title="Toplam Personel Listesi"
                        >
                            <span className="text-[11px] font-bold text-gray-800 dark:text-gray-200">{totalTeamMembers}</span>
                            <span className="text-[9px] font-semibold text-gray-500 dark:text-gray-400 ml-1">TPL</span>
                        </div>
                        <div
                            onClick={() => onOpenEmployeeModal ? onOpenEmployeeModal('presentEmployees', 'Bugün Gelen Personeller') : openModal('All')}
                            className="flex items-center px-2 py-0.5 rounded-md bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30 cursor-pointer hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
                            title="Bugün Gelen Personeller"
                        >
                            <span className="text-[11px] font-bold text-green-700 dark:text-green-400">{presentCount}</span>
                            <span className="text-[9px] font-semibold text-green-600 dark:text-green-500 ml-1">GLN</span>
                        </div>
                        <div
                            onClick={() => openModal('Absent')}
                            className="flex items-center px-2 py-0.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
                            title="Gelmeyen"
                        >
                            <span className="text-[11px] font-bold text-red-700 dark:text-red-400">{absentCount}</span>
                            <span className="text-[9px] font-semibold text-red-600 dark:text-red-500 ml-1">YOK</span>
                        </div>
                        <div
                            onClick={() => openModal('MissingRecord')}
                            className="flex items-center px-2 py-0.5 rounded-md bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/30 cursor-pointer hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors"
                            title="Risk"
                        >
                            <span className="text-[11px] font-bold text-orange-700 dark:text-orange-400">{attendanceRiskCount}</span>
                            <span className="text-[9px] font-semibold text-orange-600 dark:text-orange-500 ml-1">RSK</span>
                        </div>
                        <div
                            onClick={() => openModal('Late')}
                            className="flex items-center px-2 py-0.5 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 cursor-pointer hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                            title="Geç"
                        >
                            <span className="text-[11px] font-bold text-amber-700 dark:text-amber-400">{lateCount}</span>
                            <span className="text-[9px] font-semibold text-amber-600 dark:text-amber-500 ml-1">GEÇ</span>
                        </div>
                        <div
                            onClick={() => openModal('OnLeave')}
                            className="flex items-center px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30 cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
                            title="İzinli"
                        >
                            <span className="text-[11px] font-bold text-blue-700 dark:text-blue-400">{onLeaveCount + onReportCount}</span>
                            <span className="text-[9px] font-semibold text-blue-600 dark:text-blue-500 ml-1">İZİN</span>
                        </div>
                        {outOfOfficeCount > 0 && (
                            <div
                                onClick={() => openModal('OutOfOffice')}
                                className="flex items-center px-2 py-0.5 rounded-md bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/30 cursor-pointer hover:bg-purple-100 dark:hover:bg-purple-900/40 transition-colors"
                                title="Dış Görev"
                            >
                                <span className="text-[11px] font-bold text-purple-700 dark:text-purple-400">{outOfOfficeCount}</span>
                                <span className="text-[9px] font-semibold text-purple-600 dark:text-purple-500 ml-1">DIŞ</span>
                            </div>
                        )}

                        {!isAllClear && (
                            <button
                                onClick={() => openModal('All')}
                                className="text-[11px] font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center transition-colors px-1.5 py-1 ml-1 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30"
                            >
                                Tümünü Gör <ChevronRight size={12} className="ml-0.5" />
                            </button>
                        )}
                    </div>
                </div>

                <div className="p-3 flex-1 min-h-0 overflow-y-auto">
                    {/* Content Area */}
                    {isAllClear ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center bg-gray-50/50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 h-full">
                            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4 text-green-500 dark:text-green-400">
                                <CheckCircle2 size={32} />
                            </div>
                            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Harika!</h4>
                            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1 max-w-xs">Bugün ekibinizde dikkat gerektiren herhangi bir devamsızlık veya risk bulunmuyor.</p>
                        </div>
                    ) : (
                        <div className="space-y-2.5">
                            {previewItems.map((item, index) => {
                                const config = getStatusConfig(item.statusType);
                                const Icon = config.icon;
                                return (
                                    <motion.div
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                        key={`${item.employeeId}-${index}`}
                                        onClick={() => openModal(item.statusType || 'All')}
                                        className="flex items-center p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 border border-transparent hover:border-gray-100 dark:hover:border-gray-600 transition-all cursor-pointer group/item"
                                    >
                                        <div className={`p-1.5 rounded-md ${config.bg} ${config.color} shrink-0 mr-3 shadow-sm group-hover/item:scale-110 transition-transform`}>
                                            <Icon size={16} strokeWidth={2.5} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-[13px] font-semibold text-gray-900 dark:text-gray-100 truncate">{item.fullName}</h4>
                                                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider ${config.badge}`}>
                                                    {item.statusType === 'Late' ? 'Geç' : item.statusLabel}
                                                </span>
                                            </div>
                                            <div className="flex items-center text-[11px] text-gray-500 dark:text-gray-400">
                                                <span className="truncate">{item.departmentName}</span>
                                                {item.shortDescription && (
                                                    <>
                                                        <span className="mx-1.5 opacity-50">•</span>
                                                        <span className="truncate font-medium text-gray-600 dark:text-gray-300">{item.shortDescription}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </motion.div>

            {/* Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-hidden">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-4xl h-[90vh] max-h-[90vh] flex flex-col relative z-10 border border-gray-200 dark:border-gray-700 overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Üst Başlık */}
                            <div className="shrink-0 px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-800/80">
                                <div className="flex items-center">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 rounded-lg mr-3">
                                        <Users size={20} />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">Ekip Detayları</h2>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {filteredItems.length === allDetailItems.length
                                                ? `Toplam ${allDetailItems.length.toLocaleString('tr-TR')} kayıt`
                                                : `Filtrelenen: ${filteredItems.length.toLocaleString('tr-TR')} / ${allDetailItems.length.toLocaleString('tr-TR')} kayıt`}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Sekmeler (Tabs) */}
                            <div className="shrink-0 px-6 py-2 border-b border-gray-100 dark:border-gray-700 overflow-x-auto hide-scrollbar bg-white dark:bg-gray-800">
                                <div className="flex space-x-2">
                                    {tabs.map(tab => (
                                        <button
                                            key={tab.id}
                                            onClick={() => setSelectedTab(tab.id)}
                                            className={`px-3.5 py-1.5 text-xs sm:text-sm font-semibold rounded-lg whitespace-nowrap transition-colors flex items-center ${
                                                selectedTab === tab.id
                                                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                            }`}
                                        >
                                            {tab.label}
                                            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs tabular-nums ${
                                                selectedTab === tab.id
                                                    ? 'bg-blue-100 dark:bg-blue-800/50'
                                                    : 'bg-gray-100 dark:bg-gray-700'
                                            }`}>
                                                {(tab.count || 0).toLocaleString('tr-TR')}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Kolon Aramaları */}
                            <div className="shrink-0 px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <label className="relative block">
                                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        type="search"
                                        value={nameQuery}
                                        onChange={(e) => setNameQuery(e.target.value)}
                                        placeholder="Personel ara…"
                                        className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
                                    />
                                </label>
                                <label className="relative block">
                                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        type="search"
                                        value={deptQuery}
                                        onChange={(e) => setDeptQuery(e.target.value)}
                                        placeholder="Departman ara…"
                                        className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
                                    />
                                </label>
                                <label className="relative block">
                                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    <input
                                        type="search"
                                        value={statusQuery}
                                        onChange={(e) => setStatusQuery(e.target.value)}
                                        placeholder="Durum / vardiya / açıklama ara…"
                                        className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-900/40 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400"
                                    />
                                </label>
                            </div>

                            {/* Sayfalanmış Liste (DOM'da yalnızca aktif sayfadaki kayıtlar render edilir) */}
                            <div className="flex-1 min-h-0 overflow-y-auto p-3 bg-gray-50/30 dark:bg-gray-900/20">
                                {filteredItems.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                                        <CheckCircle2 size={48} className="mb-4 text-green-400 opacity-50" />
                                        <p>Bu kriterlere uygun kayıt bulunamadı.</p>
                                    </div>
                                ) : (
                                    <div className="grid gap-2">
                                        {paginatedItems.map((item, idx) => {
                                            const config = getStatusConfig(item.statusType);
                                            const Icon = config.icon;
                                            return (
                                                <div
                                                    key={`${item.employeeId}-${startIndex + idx}`}
                                                    className="bg-white dark:bg-gray-800 p-2.5 rounded-lg shadow-sm border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between hover:border-gray-200 dark:hover:border-gray-600 transition-colors"
                                                >
                                                    <div className="flex items-center mb-1.5 sm:mb-0 min-w-0">
                                                        <div className={`p-1.5 rounded-md ${config.bg} ${config.color} mr-3 shrink-0`}>
                                                            <Icon size={16} />
                                                        </div>
                                                        <div className="min-w-0">
                                                            <h4 className="font-semibold text-sm text-gray-800 dark:text-gray-200 truncate">{item.fullName}</h4>
                                                            <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center mt-0.5 whitespace-nowrap overflow-hidden text-ellipsis">
                                                                <span className="font-medium mr-1.5 truncate">{item.departmentName}</span>
                                                                {item.shiftLabel && (
                                                                    <>
                                                                        <span className="opacity-40 mr-1.5">•</span>
                                                                        <span className="bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-[10px] font-medium tracking-wide shrink-0">
                                                                            {item.shiftLabel}
                                                                        </span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center sm:justify-end shrink-0">
                                                        <div className="text-right flex flex-col items-start sm:items-end ml-10 sm:ml-0">
                                                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide mb-0.5 ${config.badge}`}>
                                                                {item.statusType === 'Late' ? 'Geç' : item.statusLabel}
                                                            </span>
                                                            <span className="text-[12px] font-medium text-gray-600 dark:text-gray-300">
                                                                {item.shortDescription}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Alt Sayfalama (Pagination) Barı */}
                            {filteredItems.length > 0 && (
                                <div className="shrink-0 flex flex-wrap justify-between items-center gap-3 px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800">
                                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                                        <span>
                                            Gösterilen:{' '}
                                            <strong className="text-gray-800 dark:text-gray-200">
                                                {(startIndex + 1).toLocaleString('tr-TR')}–{Math.min(startIndex + itemsPerPage, filteredItems.length).toLocaleString('tr-TR')}
                                            </strong>{' '}
                                            / {filteredItems.length.toLocaleString('tr-TR')}
                                        </span>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                            className="ml-2 px-2 py-1 rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs font-medium focus:outline-none"
                                            title="Sayfa başına kayıt sayısı"
                                        >
                                            <option value={25}>25 / sayfa</option>
                                            <option value={50}>50 / sayfa</option>
                                            <option value={100}>100 / sayfa</option>
                                        </select>
                                    </div>

                                    <div className="flex items-center space-x-1.5">
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(1)}
                                            disabled={safePage === 1}
                                            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                                            title="İlk Sayfa"
                                        >
                                            <ChevronsLeft size={15} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                            disabled={safePage === 1}
                                            className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                                        >
                                            <ChevronLeft size={14} /> Önceki
                                        </button>
                                        <span className="px-2.5 text-gray-700 dark:text-gray-300 text-xs font-semibold tabular-nums">
                                            Sayfa {safePage} / {totalPages}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                            disabled={safePage === totalPages}
                                            className="px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-40 text-xs font-semibold inline-flex items-center gap-1 transition-colors"
                                        >
                                            Sonraki <ChevronRight size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setCurrentPage(totalPages)}
                                            disabled={safePage === totalPages}
                                            className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                                            title="Son Sayfa"
                                        >
                                            <ChevronsRight size={15} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};

export default TeamStatusWidget;
